import { BookOpen } from "lucide-react"

import { Badge, Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@aix/ui"

import type { SeoTabValue } from "./SEOShortcutLegend"

interface RunbookItem {
  title: string
  signal: string
  steps: string[]
  technical: string
}

const RUNBOOKS: Record<SeoTabValue, RunbookItem[]> = {
  cockpit: [
    {
      title: "A queue is growing",
      signal: "One stage or blocker count keeps increasing.",
      steps: [
        "Open the largest stage.",
        "Select one representative item.",
        "Fix the shared cause before processing the rest.",
      ],
      technical: "Read seo.cockpit.overview, then seo.cockpit.queue with the same product and stage filters.",
    },
    {
      title: "Nothing appears to be moving",
      signal: "Draft, review, or ready counts remain unchanged.",
      steps: [
        "Check the latest run state.",
        "Open its next action and receipt.",
        "Confirm the automation is waiting rather than silently failing.",
      ],
      technical: "Inspect seo.runs.list and the digest-verified seo.runs.readReceipt response.",
    },
  ],
  operations: [
    {
      title: "An agent run failed",
      signal: "Run state is failed or partial.",
      steps: [
        "Open the run detail.",
        "Read the recorded reason and next action.",
        "Retry only the failed bounded step after its input is repaired.",
      ],
      technical: "Run checkpoints live in AIX; detailed immutable evidence is read through seo.runs.readReceipt.",
    },
    {
      title: "A release cannot advance",
      signal: "Compile, probe, approval, or activation is waiting.",
      steps: [
        "Open the release readiness checklist.",
        "Resolve the first waiting prerequisite.",
        "Never treat approval as activation.",
      ],
      technical: "The state machine is enforced by seo.releases.compile, approve, activate, and rollback.",
    },
  ],
  pages: [
    {
      title: "Review one page end to end",
      signal: "You need to decide whether a filled page is ready to advance.",
      steps: [
        "Select the page and open Draft preview; confirm the content is filled and the label says Current draft.",
        "Compare Staged and Live only when those buttons are enabled; they may intentionally show older content.",
        "Open Edit, make a small change, preview it without saving, then save it with a clear revision note.",
        "Open Revision history, inspect the diff, and test restore only when you intend to create another draft revision.",
        "Run Gate last and resolve every blocker before asking for release approval.",
      ],
      technical:
        "Draft uses seo.pages.preview/previewContent; Staged and Live use seo.pages.delivery manifest membership; revisions use expectedContentHash concurrency.",
    },
    {
      title: "A page fails quality checks",
      signal: "Gate status shows one or more blockers.",
      steps: [
        "Read the plain-language problem and impact.",
        "Open Technical evidence if needed.",
        "Repair in the structured editor, preview, save a revision, then rerun Gate.",
      ],
      technical: "seo.pages.gate writes typed findings; content changes use seo.pages.upsert with expectedContentHash.",
    },
    {
      title: "Evidence or rights are missing",
      signal: "Evidence or media blockers prevent publication.",
      steps: [
        "Attach only sources marked publishable.",
        "Confirm rights and provenance are explicit.",
        "Do not promote scraped research by changing page content.",
      ],
      technical:
        "Source publishability is controlled by seo.sources; page selection only copies an existing evidence contract.",
    },
    {
      title: "Draft, staged, and live differ",
      signal: "The page preview does not match the public URL.",
      steps: [
        "Confirm which preview state you opened.",
        "Check whether the exact slug exists in the active release.",
        "Compile and review a new release if the current draft should advance.",
      ],
      technical:
        "seo.pages.delivery resolves exact active manifest membership; draft preview never falls back to a public URL.",
    },
  ],
  templates: [
    {
      title: "A page does not match the expected template",
      signal: "The public shell, typography, or content placement looks inconsistent.",
      steps: [
        "Choose the page's versioned template and start with Sample contract.",
        "Switch to Current draft and select the real page.",
        "Click the mismatched region to identify its JSON path and renderer version.",
      ],
      technical:
        "Samples and drafts both use the registered release renderer; slot outlines exist only inside authenticated Template Lab.",
    },
  ],
  keywords: [
    {
      title: "A keyword should not become a page",
      signal: "Intent is duplicate, too weak, or outside the VidMax ICP.",
      steps: [
        "Compare the keyword with its cluster siblings.",
        "Record a decision reason.",
        "Reject or hold it instead of generating thin content.",
      ],
      technical:
        "Keyword lifecycle and decision reason remain in seo_keywords; page creation is a separate explicit step.",
    },
  ],
  publish: [
    {
      title: "Quality settings need adjustment",
      signal: "The same legitimate rule blocks an entire page family.",
      steps: [
        "Confirm the rule is wrong for the product, not merely inconvenient.",
        "Review the settings diff.",
        "Save explicitly and rerun gates for affected pages.",
      ],
      technical:
        "Only the allowlisted seo.products.updateOperatorConfig fields are editable; other product config is preserved.",
    },
    {
      title: "A compile preview looks wrong",
      signal: "Page or cluster counts are unexpected.",
      steps: [
        "Return to Pages and verify filters and gate states.",
        "Do not publish from the legacy preview.",
        "Use the immutable release workflow in Operations.",
      ],
      technical:
        "seo.publish.compile is a legacy read-only inventory preview; release pointer mutations remain MFA-gated.",
    },
  ],
  scoreboard: [
    {
      title: "A live page is not indexed",
      signal: "The URL is active but indexation remains unknown or blocked.",
      steps: [
        "Verify the exact live route and canonical.",
        "Check sitemap membership and crawl status.",
        "Wait for observations before rewriting content without evidence.",
      ],
      technical:
        "Indexation observations and GSC metrics are separate from Worker availability and release activation.",
    },
    {
      title: "Impressions grow but clicks do not",
      signal: "CTR falls while rankings or impressions improve.",
      steps: [
        "Review the query and page intent.",
        "Test a clearer title and description as a new revision.",
        "Measure the next observation window before another change.",
      ],
      technical: "Use daily seo_metrics for observation; do not overwrite historical metrics when content changes.",
    },
  ],
}

export default function SEORunbookDrawer({
  activeTab,
  open,
  onOpenChange,
}: {
  activeTab: SeoTabValue
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onOpenChange(true)}
        aria-keyshortcuts="Shift+H"
        className="fixed bottom-4 left-4 z-40 shadow-lg"
      >
        <BookOpen className="mr-1.5 h-4 w-4" /> Runbook{" "}
        <kbd className="ml-2 rounded border px-1 font-mono text-[10px]">Shift+H</kbd>
      </Button>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>SEO operator runbook</SheetTitle>
            <SheetDescription>
              Guided recovery for the current {activeTab} workflow, with the underlying contract available when needed.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {RUNBOOKS[activeTab].map((item) => (
              <article key={item.title} className="space-y-3 rounded-md border border-border p-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{item.title}</h3>
                    <Badge variant="secondary">guided</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">When: {item.signal}</p>
                </div>
                <ol className="list-decimal space-y-1 pl-5 text-sm">
                  {item.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                <details className="rounded bg-muted p-2 text-xs text-muted-foreground">
                  <summary className="cursor-pointer font-medium text-foreground">Show technical/API detail</summary>
                  <p className="mt-2 font-mono leading-relaxed">{item.technical}</p>
                </details>
              </article>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
